import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

const API_URL = import.meta.env.VITE_API_URL || ''

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
})


// Токен и профиль «Админа разработки», пока он работает под другим сотрудником.
export const IMPERSONATION_ADMIN_TOKEN_KEY = 'impersonation.adminToken'
export const IMPERSONATION_ADMIN_USER_KEY = 'impersonation.adminUser'

// Подставляет токен и в общий ключ, и в сохранённое состояние входа: интерфейс
// читает его из обоих мест. Профиль - если известен: без него разделы с
// проверкой роли до загрузки профиля уводили бы на главную.
export function restoreToken(token: string, user: unknown = null) {
    localStorage.setItem('token', token)
    try {
        const raw = localStorage.getItem('auth-storage')
        const stored = raw ? JSON.parse(raw) : { state: {}, version: 0 }
        stored.state = { ...(stored.state || {}), token, user, isAuthenticated: true }
        localStorage.setItem('auth-storage', JSON.stringify(stored))
    } catch {
        // без сохранённого состояния: профиль перечитается после перезагрузки
    }
}

api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error: AxiosError) => {
        return Promise.reject(error)
    }
)


api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.response?.status === 401) {

            const url = error.config?.url || '';
            // Вход под сотрудником истёк - возвращаем админа в его учётку, а не
            // на страницу входа.
            const adminToken = localStorage.getItem(IMPERSONATION_ADMIN_TOKEN_KEY)
            if (adminToken) {
                const adminUser = localStorage.getItem(IMPERSONATION_ADMIN_USER_KEY)
                localStorage.removeItem(IMPERSONATION_ADMIN_TOKEN_KEY)
                localStorage.removeItem(IMPERSONATION_ADMIN_USER_KEY)
                restoreToken(adminToken, adminUser ? JSON.parse(adminUser) : null)
                window.location.href = '/admin/operators'
                return Promise.reject(error)
            }
            if (!url.includes('/auth/login') && !window.location.pathname.includes('/login')) {

                localStorage.clear()
                window.location.href = '/login'
            }
        }
        return Promise.reject(error)
    }
)


export const authApi = {
    // device_token - ключ браузера, где уже вводили код: сутки код не нужен.
    login: (username: string, password: string, deviceToken?: string | null) =>
        api.post('/auth/login', { username, password, device_token: deviceToken || null }),

    // Второй шаг входа по коду и повторная отправка кода.
    verifyOtp: (challengeId: string, code: string) =>
        api.post('/auth/otp/verify', { challenge_id: challengeId, code }),

    resendOtp: (challengeId: string) =>
        api.post('/auth/otp/resend', { challenge_id: challengeId }),

    getMe: () => api.get('/auth/me'),

    // Отзывает все токены сотрудника на сервере - см. POST /auth/logout.
    logout: () => api.post('/auth/logout'),

    // «Админ разработки» входит под сотрудником: токен на его имя.
    impersonate: (operatorId: number) => api.post<{ access_token: string }>(`/auth/impersonate/${operatorId}`),

    // Свой пароль - любая роль, с вводом текущего. В ответе новый токен:
    // смена пароля закрывает все прежние входы, включая этот.
    changePassword: (currentPassword: string, newPassword: string) =>
        api.post<{ message: string; access_token: string }>('/auth/change-password', {
            current_password: currentPassword, new_password: newPassword,
        }),
}


export const operatorsApi = {
    getAll: (skip = 0, limit = 100) =>
        api.get(`/operators?skip=${skip}&limit=${limit}`),

    get: (id: number) => api.get(`/operators/${id}`),

    create: (data: { username: string; password: string; full_name?: string; role?: string; department?: string | null; visibility_profile_ids?: number[] }) =>
        api.post('/operators', data),

    update: (id: number, data: { username?: string; full_name?: string; role?: string; department?: string | null; is_active?: boolean; password?: string; visibility_profile_ids?: number[] }) =>
        api.put(`/operators/${id}`, data),

    delete: (id: number) => api.delete(`/operators/${id}`),

    // Свободна ли личная почта: у каждого сотрудника она своя.
    emailAvailable: (email: string, excludeId?: number) =>
        api.get<{ available: boolean; detail: string | null }>('/operators/email-available', { params: { email, exclude_id: excludeId } }),

    uploadAvatar: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post('/operators/me/avatar', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    // Свои данные - любая роль. Личная почта меняется только с текущим паролем.
    updateProfile: (data: { username?: string; full_name?: string; avatar_url?: string; wiseway_email?: string; current_password?: string }) =>
        api.patch('/operators/me', data),
}


export interface AccountBlockEvent {
    id: number
    block_type: string
    health_status?: string | null
    reason?: string | null
    is_block: boolean
    occurred_at: string
    blocked_until?: string | null
    resolved_at?: string | null
    span_started_at?: string | null
    messages_sent: number
    unique_leads: number
}

export interface AccountStatsRow {
    id: number
    phone: string
    username?: string | null
    name?: string | null
    status: string
    health_status?: string | null
    block_type?: string | null
    blocked_until?: string | null
    pool_geo?: string | null
    pool_temperature?: string | null
    is_connected: boolean
    created_at?: string | null
    alive_days?: number | null
    sent_total: number
    leads_total: number
    received_total: number
    sent_period: number
    // Отправлено за календарные сутки по Москве.
    sent_today: number
    sent_yesterday: number
    leads_period: number
    first_sent_at?: string | null
    last_sent_at?: string | null
    sent_per_day: number
    blocks_count: number
    avg_sent_before_block?: number | null
    avg_leads_before_block?: number | null
    avg_days_before_block?: number | null
    last_block_at?: string | null
    last_block_type?: string | null
    last_block_reason?: string | null
    last_block_sent?: number | null
    last_block_leads?: number | null
    last_block_resolved_at?: string | null
}

export interface AccountsTimelinePoint {
    date: string
    sent: number
    leads: number
    blocks: number
}

export interface AccountsStatistics {
    days: number
    timeline: AccountsTimelinePoint[]
    filters: { geos?: string[] }
    summary: {
        accounts_total: number
        accounts_active: number
        accounts_blocked_now: number
        sent_total: number
        leads_total: number
        sent_period: number
        blocks_total: number
        blocks_period: number
        avg_sent_before_block?: number | null
        avg_leads_before_block?: number | null
        avg_days_before_block?: number | null
        accounts_never_blocked: number
    }
    accounts: AccountStatsRow[]
    block_types: { block_type: string; count: number; avg_sent_before?: number | null; last_at?: string | null }[]
}

export interface CloudPasswordState {
    has_password: boolean
    hint?: string | null
    has_recovery: boolean
    email_unconfirmed_pattern?: string | null
    pending_reset_date?: string | null
}

export const accountsApi = {
    getAll: (skip = 0, limit = 100, includeInactive = false) =>
        api.get('/accounts', { params: { skip, limit, include_inactive: includeInactive } }),

    // Статистика работы аккаунтов: выработка и блокировки.
    getStatistics: (params: { days?: number; geo?: string; temperature?: string; health?: string } = {}) =>
        api.get<AccountsStatistics>('/accounts/statistics', { params }),

    getBlockEvents: (id: number) => api.get<AccountBlockEvent[]>(`/accounts/${id}/block-events`),

    refreshStatuses: () => api.post('/accounts/refresh-statuses'),

    get: (id: number) => api.get(`/accounts/${id}`),

    create: (data: { phone: string; proxy_id?: number; assigned_operator_id?: number; pool_geo?: string | null; pool_temperature?: string | null }) =>
        api.post('/accounts', data),

    update: (id: number, data: { proxy_id?: number | null; assigned_operator_id?: number; pool_enabled?: boolean; pool_geo?: string | null; pool_temperature?: string | null; pool_weight?: number; daily_send_limit?: number | null }) =>
        api.patch(`/accounts/${id}`, data),

    updateProfile: (id: number, data: { first_name?: string; last_name?: string; username?: string; bio?: string; avatar?: File | null; remove_avatar?: boolean }) => {
        const formData = new FormData()
        if (data.first_name !== undefined) formData.append('first_name', data.first_name)
        if (data.last_name !== undefined) formData.append('last_name', data.last_name)
        if (data.username !== undefined) formData.append('username', data.username)
        if (data.bio !== undefined) formData.append('bio', data.bio)
        if (data.remove_avatar) formData.append('remove_avatar', 'true')
        if (data.avatar) formData.append('avatar', data.avatar)
        return api.patch(`/accounts/${id}/profile`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    // Облачный пароль (2FA) аккаунта. Пусто в new_password — снять пароль.
    getCloudPassword: (id: number) =>
        api.get<CloudPasswordState>(`/accounts/${id}/cloud-password`),
    setCloudPassword: (id: number, data: { new_password?: string; current_password?: string; hint?: string }) =>
        api.post(`/accounts/${id}/cloud-password`, data),

    getFirstMessageSettings: () =>
        api.get('/accounts/first-message-settings'),

    updateFirstMessageSettings: (data: any) =>
        api.put('/accounts/first-message-settings', data),

    delete: (id: number) => api.delete(`/accounts/${id}`),

    importTdata: (id: number, file: File, archivePassword?: string) => {
        const formData = new FormData()
        formData.append('tdata_file', file)
        if (archivePassword) formData.append('archive_password', archivePassword)
        return api.post(`/accounts/${id}/import-tdata`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    connect: (id: number) => api.post(`/accounts/${id}/connect`),

    disconnect: (id: number) => api.post(`/accounts/${id}/disconnect`),

    getSessions: (id: number) => api.get(`/accounts/${id}/sessions`),

    // Внешний адрес аккаунта: проверка тем же путём, каким он ходит в Telegram.
    checkExitIp: (id: number) => api.post(`/accounts/${id}/exit-ip`),

    revokeSession: (id: number, sessionHash: string) =>
        api.post(`/accounts/${id}/sessions/revoke`, { session_hash: sessionHash }),

    revokeOtherSessions: (id: number) =>
        api.post(`/accounts/${id}/sessions/revoke-others`),

    createFromTdata: (file: File, proxyId?: number, operatorId?: number, poolGeo?: string, archivePassword?: string) => {
        const formData = new FormData()
        formData.append('tdata_file', file)
        if (proxyId) formData.append('proxy_id', proxyId.toString())
        if (operatorId) formData.append('assigned_operator_id', operatorId.toString())
        if (poolGeo) formData.append('pool_geo', poolGeo)
        if (archivePassword) formData.append('archive_password', archivePassword)
        return api.post('/accounts/create-from-tdata', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    bulkCreateFromTdata: (files: File[], geo?: string, operatorId?: number, archivePassword?: string) => {
        const formData = new FormData()
        files.forEach((file) => formData.append('tdata_files', file))
        if (geo) formData.append('geo', geo)
        if (operatorId) formData.append('assigned_operator_id', operatorId.toString())
        if (archivePassword) formData.append('archive_password', archivePassword)
        return api.post('/accounts/bulk-create-from-tdata', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },


    sendCode: (id: number) => api.post(`/accounts/${id}/send-code`),

    resendCode: (id: number, phoneCodeHash: string) =>
        api.post(`/accounts/${id}/resend-code`, null, {
            params: { phone_code_hash: phoneCodeHash }
        }),

    verifyCode: (id: number, code: string, phoneCodeHash: string) =>
        api.post(`/accounts/${id}/verify-code`, null, {
            params: { code, phone_code_hash: phoneCodeHash }
        }),

    verify2fa: (id: number, password: string) =>
        api.post(`/accounts/${id}/verify-2fa`, null, {
            params: { password }
        }),


    startQrLogin: (proxyId?: number, poolGeo?: string, poolTemperature?: string) =>
        api.post('/accounts/qr-login/start', null, {
            params: { proxy_id: proxyId, pool_geo: poolGeo, pool_temperature: poolTemperature }
        }),

    checkQrLogin: (id: number) =>
        api.post(`/accounts/${id}/check-qr-login`),

    qr2fa: (id: number, password: string) =>
        api.post(`/accounts/${id}/qr-2fa`, null, {
            params: { password }
        }),


    getFolders: (id: number) =>
        api.get(`/accounts/${id}/folders`),

    getDialogs: (id: number, limit = 0, folderId?: number, offsetDate: number | string = 0, offsetId = 0, offsetPeerId?: number, live = false) =>
        api.get(`/accounts/${id}/dialogs`, { params: { limit, folder_id: folderId, offset_date: offsetDate, offset_id: offsetId, offset_peer_id: offsetPeerId, live } }),

    getDialogCounts: () =>
        api.get<{ counts: Record<number, number> }>('/accounts/dialog-counts'),

    getTopics: (id: number, chatId: number, limit = 100) =>
        api.get(`/accounts/${id}/topics/${chatId}`, { params: { limit } }),

    getMessages: (id: number, chatId: number, limit = 50, skip = 0, topicId?: number) =>
        api.get(`/accounts/${id}/messages/${chatId}`, { params: { limit, skip, topic_id: topicId } }),

    sendMessage: (id: number, chatId: number, text: string, replyToMsgId?: number, topicId?: number) =>
        api.post(`/accounts/${id}/send/${chatId}`, null, { params: { text, reply_to_msg_id: replyToMsgId, topic_id: topicId } }),

    sendMedia: (id: number, chatId: number, file: File, caption?: string, topicId?: number, replyToMsgId?: number) => {
        const formData = new FormData()
        formData.append('file', file)
        if (caption) formData.append('caption', caption)
        if (topicId !== undefined) formData.append('topic_id', String(topicId))
        if (replyToMsgId !== undefined) formData.append('reply_to_msg_id', String(replyToMsgId))
        return api.post(`/accounts/${id}/send-media/${chatId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    sendToNewContact: (id: number, username?: string, phone?: string, text?: string) =>
        api.post(`/accounts/${id}/send-new`, null, { params: { username, phone, text } }),

    deleteChat: (id: number, chatId: number, deleteInTelegram = false) =>
        api.delete(`/accounts/${id}/delete-chat/${chatId}`, { params: { delete_in_telegram: deleteInTelegram } }),

    markAsRead: (id: number, chatId: number, topicId?: number, maxMessageId?: number) =>
        api.post(`/accounts/${id}/mark-read/${chatId}`, null, { params: { topic_id: topicId, max_message_id: maxMessageId } }),

    blockUser: (id: number, chatId: number) =>
        api.post(`/accounts/${id}/block-user/${chatId}`),
    unblockUser: (id: number, chatId: number) =>
        api.post(`/accounts/${id}/unblock-user/${chatId}`),


    getPinnedMessages: (id: number, chatId: number, topicId?: number) =>
        api.get(`/accounts/${id}/pinned/${chatId}`, { params: { topic_id: topicId } }),
    pinMessage: (id: number, chatId: number, messageId: number) =>
        api.post(`/accounts/${id}/pin/${chatId}/${messageId}`),
    unpinMessage: (id: number, chatId: number, messageId: number) =>
        api.post(`/accounts/${id}/unpin/${chatId}/${messageId}`),


    editMessage: (id: number, chatId: number, messageId: number, text: string) =>
        api.post(`/accounts/${id}/edit-message/${chatId}/${messageId}`, null, { params: { text } }),
    deleteMessages: (id: number, chatId: number, messageIds: number[], revoke = true) =>
        api.post(`/accounts/${id}/delete-messages/${chatId}`, messageIds, { params: { revoke } }),
    forwardMessages: (id: number, fromChatId: number, toChatIds: number[], messageIds: number[]) =>
        api.post(`/accounts/${id}/forward-messages`, { from_chat_id: fromChatId, to_chat_ids: toChatIds, message_ids: messageIds }),


    searchUser: (id: number, query: string) =>
        api.get(`/accounts/${id}/search-user`, { params: { query } }),


    syncAvatars: (id: number, chatIds: number[]) =>
        api.post(`/accounts/${id}/sync-avatars`, chatIds),

    checkProxy: (id: number) => api.get<{ success: boolean, message: string, ip?: string, proxy?: string }>(`/accounts/${id}/check-proxy`),
}


export const chatsApi = {
    getAll: (skip = 0, limit = 50, archived = false) =>
        api.get(`/chats?skip=${skip}&limit=${limit}&archived=${archived}`),

    get: (id: number) => api.get(`/chats/${id}`),

    getMessages: (chatId: number, skip = 0, limit = 50) =>
        api.get(`/chats/${chatId}/messages?skip=${skip}&limit=${limit}`),

    sendMessage: (chatId: number, text: string) =>
        api.post(`/chats/${chatId}/messages`, { chat_id: chatId, text_content: text }),

    markAsRead: (chatId: number, resetCounter = true) =>
        api.post(`/chats/${chatId}/read?reset_counter=${resetCounter}`),

    archive: (chatId: number) => api.post(`/chats/${chatId}/archive`),

    unarchive: (chatId: number) => api.post(`/chats/${chatId}/unarchive`),

    exportExcel: (all: boolean = true, ids?: number[]) =>
        api.get('/chats/export/excel', {
            params: { all, ids: ids?.join(',') },
            responseType: 'blob'
        }),
}


export const contactsApi = {
    getAll: (skip = 0, limit = 50, search?: string, tag?: string) => {
        let url = `/contacts?skip=${skip}&limit=${limit}`
        if (search) url += `&search=${encodeURIComponent(search)}`
        if (tag) url += `&tag=${encodeURIComponent(tag)}`
        return api.get(url)
    },

    get: (id: number) => api.get(`/contacts/${id}`),

    create: (data: { username?: string; phone?: string; first_name?: string; last_name?: string; notes?: string; tags?: string[] }) =>
        api.post('/contacts', data),

    update: (id: number, data: { tags?: string[]; notes?: string; is_blocked?: boolean; first_name?: string; last_name?: string }) =>
        api.put(`/contacts/${id}`, data),

    delete: (id: number) => api.delete(`/contacts/${id}`),

    addTag: (id: number, tag: string) =>
        api.post(`/contacts/${id}/add-tag?tag=${encodeURIComponent(tag)}`),

    removeTag: (id: number, tag: string) =>
        api.post(`/contacts/${id}/remove-tag?tag=${encodeURIComponent(tag)}`),

    block: (id: number) => api.post(`/contacts/${id}/block`),

    unblock: (id: number) => api.post(`/contacts/${id}/unblock`),

    getAllTags: () => api.get('/contacts/tags/list'),
}


export const proxiesApi = {
    getAll: (skip = 0, limit = 100) =>
        api.get(`/proxies?skip=${skip}&limit=${limit}`),

    get: (id: number) => api.get(`/proxies/${id}`),

    create: (data: {
        name: string
        type: string
        host: string
        port: number
        username?: string
        password?: string
        country?: string
        is_mobile?: boolean
    }) => api.post('/proxies', data),

    update: (id: number, data: { name?: string; host?: string; port?: number; status?: string }) =>
        api.put(`/proxies/${id}`, data),

    delete: (id: number) => api.delete(`/proxies/${id}`),

    check: (id: number) => api.post(`/proxies/${id}/check`),

    bulkCreate: (data: { proxies_text: string; geo: string; type?: string; is_mobile?: boolean }) =>
        api.post('/proxies/bulk', data),
}


export const notificationsApi = {
    getAll: (params?: any) => api.get('/notifications', { params }),
    markRead: (id: number) => api.post(`/notifications/${id}/read`),
    markAllRead: () => api.post('/notifications/read-all'),
    getUnreadCount: () => api.get('/notifications/unread-count'),
}


export const internalChatsApi = {
    getMyChats: () => api.get('/internal/chats'),
    startChat: (recipientId: number) => api.post(`/internal/chats?recipient_id=${recipientId}`),
    getMessages: (chatId: number, params?: any) => api.get(`/internal/chats/${chatId}/messages`, { params }),
    sendMessage: (data: { recipient_id: number, text_content: string }) => api.post('/internal/chats/messages', data),
    getUnreadCount: () => api.get('/internal/chats/unread-count'),
}


export const statsApi = {
    getDashboard: (operatorId?: number) => api.get('/stats/dashboard', { params: { operator_id: operatorId } }),

    getOperators: () => api.get('/stats/operators'),

    getAccounts: () => api.get('/stats/accounts'),

    getMyStats: () => api.get('/stats/my-stats'),
}

export default api
